import { ref } from 'vue'
import { defineStore } from 'pinia'

import type { IUser } from '@/types/users/IUser'
import type { AuthenticatedUserDepartment } from '@/shared/model/types/AuthenticatedUser/AuthenticatedUserDepartment'
import type { AuthenticatedUser } from '@/shared/model/types/AuthenticatedUser/AuthenticatedUser'
import {
  defaultUserPreferences,
  type UserPreferenceKey,
  type UserPreferences,
} from '@/shared/model/types/UserPreferences/UserPreference'

export const SELECTED_DEPARTMENT_STORAGE_KEY = 'selectedDepartment'

export const useUserStore = defineStore('user', () => {
  const storeAuthenticatedUser = ref<AuthenticatedUser | null>(null)
  const storeUsers = ref<IUser[]>([])
  const storeCurrentDepartment = ref<AuthenticatedUserDepartment | null>(null)

  function storeSetAuthenticatedUser(u: AuthenticatedUser | null) {
    storeAuthenticatedUser.value = u
      ? {
          ...u,
          userPreferences: {
            ...defaultUserPreferences,
            ...u.userPreferences,
          },
        }
      : null

    // Set the current department
    if (u && u.departments && u.departments.length > 0) {
      // Try to get the department from local storage
      let storedDepartment: AuthenticatedUserDepartment | null = null
      try {
        const storedDepartmentJson = localStorage.getItem(SELECTED_DEPARTMENT_STORAGE_KEY)
        if (storedDepartmentJson) {
          const parsedDepartment = JSON.parse(storedDepartmentJson)
          // Check if the stored department is still available to the user
          storedDepartment = u.departments.find(dept => dept.id === parsedDepartment.id) || null
        }
      }
      catch (error) {
        console.error('Error retrieving stored department:', error)
      }

      // If we have a valid stored department, use it
      if (storedDepartment) {
        storeCurrentDepartment.value = storedDepartment
      }
      else {
        // Otherwise use the primary department or the first one
        const primaryDepartment = u.departments.find(dept => dept.isPrimary)
        storeCurrentDepartment.value = primaryDepartment || u.departments[0]

        // Save this department to local storage
        if (storeCurrentDepartment.value) {
          localStorage.setItem(SELECTED_DEPARTMENT_STORAGE_KEY, JSON.stringify(storeCurrentDepartment.value))
        }
      }
    }
    else {
      storeCurrentDepartment.value = null
      // Clear the stored department if there are no departments
      localStorage.removeItem(SELECTED_DEPARTMENT_STORAGE_KEY)
    }
  }

  function storeSetAuthenticatedUsers(u: IUser[]) {
    storeUsers.value = u
  }

  function storeResetUsers() {
    storeUsers.value = []
  }

  function storeSetCurrentDepartment(department: AuthenticatedUserDepartment) {
    // First update local storage
    localStorage.setItem(SELECTED_DEPARTMENT_STORAGE_KEY, JSON.stringify(department))
    // Then update the store
    storeCurrentDepartment.value = department
  }

  function storeGetCurrentDepartment() {
    if (!storeCurrentDepartment.value) {
      throw new Error('Current department is not set')
    }

    return storeCurrentDepartment.value
  }

  function storeGetUserPreference<K extends UserPreferenceKey>(key: K): UserPreferences[K] {
    return storeAuthenticatedUser.value?.userPreferences[key] ?? defaultUserPreferences[key]
  }

  function storeSetUserPreference<K extends UserPreferenceKey>(key: K, value: UserPreferences[K]): void {
    if (!storeAuthenticatedUser.value) {
      return
    }

    storeAuthenticatedUser.value = {
      ...storeAuthenticatedUser.value,
      userPreferences: {
        ...storeAuthenticatedUser.value.userPreferences,
        [key]: value,
      } as UserPreferences,
    }
  }

  return {
    storeSetAuthenticatedUser,
    storeAuthenticatedUser,
    storeSetAuthenticatedUsers,
    storeUsers,
    storeResetUsers,
    storeCurrentDepartment,
    storeSetCurrentDepartment,
    storeGetCurrentDepartment,
    storeGetUserPreference,
    storeSetUserPreference,
  }
})
